/**
 * @author Dimitry Kudrayvtsev
 * @version 2.1
 */

import * as d3 from "d3";
import moment from "moment";

const ganttGen = function(options = {}) {
  let FIT_TIME_DOMAIN_MODE = "fit";
  let FIXED_TIME_DOMAIN_MODE = "fixed";

  let margin = {
    top: 10,
    right: 20,
    bottom: 10,
    left: 60,
    ...options.margin,
  };
  let selector = "body";
  let timeDomainStart = d3.timeDay.offset(new Date(), -3);
  let timeDomainEnd = d3.timeDay.offset(new Date(), +3);
  let timeDomainMode = FIT_TIME_DOMAIN_MODE;  // fixed or fit
  let taskTypes = [];
  let taskStatus = [];
  let height = 130 - margin.top - margin.bottom - 5;
  let width = 874 - margin.right - margin.left - 5;

  let tickFormat = "YYYY-MM-DD";

  let keyFunction = function(d) {
    return d.startDate + d.taskName + d.endDate;
  };

  let rectTransform = function(d) {
    return "translate(" + x(d.startDate) + "," + y(d.taskName) + ")";
  };

  let x = d3.scaleTime().domain([timeDomainStart, timeDomainEnd]).range([0, width]).clamp(true);

  let y = d3.scaleBand().domain(taskTypes).range([0, height - margin.top - margin.bottom]);

  let xAxis = d3.axisBottom(x).tickFormat(d3.timeFormat(tickFormat))
    .tickSize(8).tickPadding(8);

  let yAxis = d3.axisLeft(y).tickSize(0);

  let initTimeDomain = function(tasks) {
    if (timeDomainMode === FIT_TIME_DOMAIN_MODE) {
      if (tasks === undefined || tasks.length < 1) {
        timeDomainStart = d3.timeDay.offset(new Date(), -3);
        timeDomainEnd = d3.timeHour.offset(new Date(), +3);
        return;
      }
      tasks.sort(function(a, b) {
        return a.endDate - b.endDate;
      });
      timeDomainEnd = tasks[tasks.length - 1].endDate;
      tasks.sort(function(a, b) {
        return a.startDate - b.startDate;
      });
      timeDomainStart = tasks[0].startDate;
      let offsetDays = d3.timeDay.count(timeDomainStart, timeDomainEnd) || 1;
      // 需要额外在前后增加的距离，用来显示label，85是label的宽度
      let offset = 85 * (offsetDays + 8) / (width - 85 * 2);
      offset = offset > 0 ? Math.ceil(offset) : Math.floor(offset);
      timeDomainStart = d3.timeDay.offset(timeDomainStart, -offset);
      if ((offsetDays + 2 * offset) % 8) {
        // 底部要显示8条tick，必须要是8的倍数
        // +1是因为d3.timeDay.range(start, stop[, step]) ，不会返回最后stop
        offset = 8 - (offsetDays + 2 * offset) % 8 + 1 + offset;
      }
      timeDomainEnd = d3.timeDay.offset(timeDomainEnd, offset);
    }
  };

  let initAxis = function() {
    x = d3.scaleTime().domain([timeDomainStart, timeDomainEnd]).range([0, width]).clamp(false);
    y = d3.scaleBand().domain(taskTypes).range([0, height - margin.top - margin.bottom]).padding(.4);
    let offsetDays = d3.timeDay.count(timeDomainStart, timeDomainEnd);
    let step = Math.round(offsetDays / 8) || 1;
    xAxis = d3.axisBottom(x).tickFormat((date, index) => {
      date = moment(date);
      if (index === 0) {
        return date.format("YYYY-MM-DD");
      }
      return date.format("MM-DD");
    }).tickSize(4).tickPadding(8).tickValues(d3.timeDay.range(timeDomainStart, timeDomainEnd, step));
    yAxis = d3.axisLeft(y).tickSize(0).tickPadding(12);
  };

  function gantt(tasks) {
    initTimeDomain(tasks);
    initAxis();

    let svg = d3.select(selector)
      .append("svg")
      .attr("class", "chart")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("class", "gantt-chart")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .attr("transform", "translate(" + margin.left + ", " + margin.top + ")");

    const $svg = svg.selectAll(".chart")
      .data(tasks, keyFunction).enter();

    $svg
      .append("rect")
      .attr("class", function(d) {
        if (taskStatus[d.status] == null) { return "bar"; }
        return `bar ${taskStatus[d.status]}`;
      })
      .attr("y", function(d) {
        return taskTypes.length > 1 ? 0 : 7;
      })
      .attr("transform", rectTransform)
      .attr("height", function(d) {
        return 20;
      })
      .attr("width", function(d) {
        return Math.max(1, (x(d.endDate) - x(d.startDate)));
      });

    $svg
      .append("text")
      .attr("x", function(d) {
        return -85;
      })
      .attr("y", function(d) {
        return taskTypes.length > 1 ? 15 : 22;
      })
      .attr("transform", rectTransform)
      .text(function(d) {
        return moment(d.startDate).format("YYYY-MM-DD");
      });

    $svg
      .append("text")
      .attr("x", function(d) {
        return 12 + Math.max(0, (x(d.endDate) - x(d.startDate)));
      })
      .attr("y", function(d) {
        return taskTypes.length > 1 ? 15 : 22;
      })
      .attr("transform", rectTransform)
      .text(function(d) {
        return moment(d.endDate).format("YYYY-MM-DD");
      });

    $svg.append("line")
      .attr("x1", function(d) {
        return x(d.startDate);
      })
      .attr("class", "line-to-axis")
      .attr("y1", function(d) {
        return y(d.taskName);
      })
      .attr("x2", function(d) {
        return x(d.startDate);
      })
      .attr("y2", y.range()[1]);

    $svg.append("line")
      .attr("x1", function(d) {
        return x(d.startDate) + Math.max(1, (x(d.endDate) - x(d.startDate)));
      })
      .attr("class", "line-to-axis")
      .attr("y1", function(d) {
        return y(d.taskName);
      })
      .attr("x2", function(d) {
        return x(d.startDate) + Math.max(1, (x(d.endDate) - x(d.startDate)));
      })
      .attr("y2", y.range()[1]);

    svg.append("g")
      .attr("class", "x axis")
      .attr("transform", "translate(0, " + (height - margin.top - margin.bottom) + ")")
      .transition()
      .call(xAxis);

    svg.append("g").attr("class", "y axis").call(yAxis);

    return gantt;
  }

  gantt.redraw = function(tasks) {
    d3.select(".chart").remove();
    gantt(tasks);
    return gantt;
  };

  gantt.margin = function(value) {
    if (!arguments.length) {
      return margin;
    }
    margin = value;
    return gantt;
  };

  gantt.timeDomain = function(value) {
    if (!arguments.length) {
      return [timeDomainStart, timeDomainEnd];
    }
    timeDomainStart = +value[0], timeDomainEnd = +value[1];
    return gantt;
  };

  /**
   * @param {string}
   *                vale The value can be "fit" - the domain fits the data or
   *                "fixed" - fixed domain.
   */
  gantt.timeDomainMode = function(value) {
    if (!arguments.length) {
      return timeDomainMode;
    }
    timeDomainMode = value;
    return gantt;

  };

  gantt.taskTypes = function(value) {
    if (!arguments.length) {
      return taskTypes;
    }
    taskTypes = value;
    return gantt;
  };

  gantt.taskStatus = function(value) {
    if (!arguments.length) {
      return taskStatus;
    }
    taskStatus = value;
    return gantt;
  };

  gantt.width = function(value) {
    if (!arguments.length) {
      return width;
    }
    width = +value;
    return gantt;
  };

  gantt.height = function(value) {
    if (!arguments.length) {
      return height;
    }
    height = +value;
    return gantt;
  };

  gantt.tickFormat = function(value) {
    if (!arguments.length) {
      return tickFormat;
    }
    tickFormat = value;
    return gantt;
  };

  gantt.selector = function(value) {
    if (!arguments.length) {
      return selector;
    }
    selector = value;
    return gantt;
  };

  return gantt;
};

export default ganttGen;
